import { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import { contact } from "@/config/site";
import { currency, formatPrice } from "@/config/products";

const CartContext = createContext(null);
const CART_KEY = "kelo_cart";

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

const loadCart = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadCart);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  // product: { id, key } ; store identifier + size + price; name resolved by locale
  const addItem = useCallback((product, size, price, qty = 1) => {
    setItems((prev) => {
      const key = `${product.id}-${size}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [...prev, { key, id: product.id, productKey: product.key, size, price, qty }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const updateQty = useCallback((key, qty) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty: Math.max(1, qty) } : i)));
  }, []);
  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const total = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);

  const value = { items, open, setOpen, addItem, removeItem, updateQty, clear, count, total };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// --- WhatsApp helpers ---
// Devuelve true cuando el número real de WhatsApp está configurado (no placeholder).
// Cuando no lo esté, los CTAs de WhatsApp se ocultan/desactivan.
export const whatsappConfigured = () => {
  const n = String(contact.whatsappNumber || "").trim();
  return !!n && !/^\[.*\]$/.test(n) && /\d{7,}/.test(n.replace(/\D/g, ""));
};

export const buildWhatsappUrl = (message) => {
  const num = String(contact.whatsappNumber).replace(/\D/g, "");
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
};

// t = current locale object; resolves localized product name by productKey
export const cartWhatsappUrl = (items, total, t) => {
  const lines = items
    .map((i) =>
      t.whatsapp.line({
        name: t.products[i.productKey]?.name || i.productKey,
        size: i.size,
        qty: i.qty,
        unit: formatPrice(i.price),
      })
    )
    .join("\n\n");
  return buildWhatsappUrl(t.whatsapp.order(lines, formatPrice(total)));
};

export const singleWhatsappUrl = ({ productKey, size, qty, price }, t) => {
  const line = t.whatsapp.line({
    name: t.products[productKey]?.name || productKey,
    size,
    qty,
    unit: formatPrice(price),
  });
  return buildWhatsappUrl(t.whatsapp.order(line, formatPrice(price * qty)));
};

export const consultWhatsappUrl = (t) => buildWhatsappUrl(t.whatsapp.consult);
export const supportWhatsappUrl = (t) => buildWhatsappUrl(t.whatsapp.support);
export { currency };
